const toArray = (value) => (Array.isArray(value) ? value : []);
const getEntryEffectId = (entry) => entry?.effectId || entry?.id || null;
const normalizeEffectIndex = (value) => (Number.isInteger(value) ? value : null);

const addCleanupName = (set, value) => {
  if (typeof value === 'string' && value) {
    set.add(value);
  }
};

const getCleanupNamesForEntry = (entry, spellName = null) => {
  const names = new Set();
  addCleanupName(names, spellName);
  addCleanupName(names, entry?.name);
  addCleanupName(names, entry?.spellName);
  addCleanupName(names, entry?.concentrationSpellName);
  addCleanupName(names, entry?.legacyBuffName);
  addCleanupName(names, entry?.legacyConditionName);
  addCleanupName(names, entry?.conditionName);
  return [...names];
};

const getPrimaryLegacyLabel = (entry, spellName = null) => (
  entry?.legacyBuffName
  || entry?.legacyConditionName
  || entry?.conditionName
  || entry?.spellName
  || entry?.name
  || spellName
  || null
);

const buildLegacyRemovalCounts = (entries, spellName = null) => {
  const counts = new Map();
  toArray(entries).forEach((entry) => {
    const label = getPrimaryLegacyLabel(entry, spellName);
    if (!label) return;
    counts.set(label, (counts.get(label) || 0) + 1);
  });
  return counts;
};

const consumeLegacyLabels = (entries, removalCounts, fallbackNames = []) => {
  if (removalCounts.size === 0 && fallbackNames.length === 0) return toArray(entries);
  const remainingCounts = new Map(removalCounts);
  return toArray(entries).filter((entry) => {
    const label = typeof entry === 'string'
      ? entry
      : (typeof entry?.name === 'string' ? entry.name : '');
    const exactCount = remainingCounts.get(label) || 0;
    if (exactCount > 0) {
      remainingCounts.set(label, exactCount - 1);
      return false;
    }
    if (fallbackNames.length > 0 && fallbackNames.some((name) => label.includes(name))) {
      return false;
    }
    return true;
  });
};

const matchesEffectGroup = (entry, effectGroupId) => (
  !!effectGroupId
  && typeof entry?.effectGroupId === 'string'
  && entry.effectGroupId === effectGroupId
);

export const isLinkedConcentrationEffect = (
  entry,
  { casterId, spellName = null, effectGroupId = null } = {},
) => {
  if (!entry) return false;
  if (matchesEffectGroup(entry, effectGroupId)) return true;
  if (effectGroupId && typeof entry?.effectGroupId === 'string' && entry.effectGroupId !== effectGroupId) return false;
  if (!casterId || entry.casterId !== casterId) return false;
  const duration = entry.duration;
  if (duration !== 'concentration' && duration !== null) return false;
  if (!spellName) return true;
  return getCleanupNamesForEntry(entry).includes(spellName);
};

export const isLinkedConcentrationMark = (
  entry,
  { casterId, spellName = null, effectGroupId = null } = {},
) => {
  if (!entry) return false;
  if (matchesEffectGroup(entry, effectGroupId)) return true;
  if (effectGroupId && typeof entry?.effectGroupId === 'string' && entry.effectGroupId !== effectGroupId) return false;
  if (!casterId || entry.casterId !== casterId) return false;
  if (!spellName) return true;
  return getCleanupNamesForEntry(entry).includes(spellName);
};

const filterConditionLabels = (conditions, blockedNames) => {
  if (!blockedNames.size) return toArray(conditions);
  return toArray(conditions).filter((condition) => {
    const label = typeof condition === 'string'
      ? condition
      : (typeof condition?.name === 'string' ? condition.name : '');
    return ![...blockedNames].some((name) => label.includes(name));
  });
};

export const createRuntimeEffectRef = ({
  effectId = null,
  effectGroupId = null,
  sourceCollection = null,
  effectIndex = null,
  effectName = null,
  casterId = null,
} = {}) => ({
  effectId: typeof effectId === 'string' && effectId ? effectId : null,
  effectGroupId: typeof effectGroupId === 'string' && effectGroupId ? effectGroupId : null,
  sourceCollection: typeof sourceCollection === 'string' && sourceCollection ? sourceCollection : null,
  effectIndex: normalizeEffectIndex(effectIndex),
  effectName: typeof effectName === 'string' && effectName ? effectName : null,
  casterId: typeof casterId === 'string' && casterId ? casterId : null,
});

export const getRuntimeEffectRefKey = (ref = {}) => {
  if (ref?.effectId) return `id:${ref.effectId}`;
  if (ref?.sourceCollection && Number.isInteger(ref?.effectIndex)) {
    return `src:${ref.sourceCollection}:${ref.effectIndex}`;
  }
  if (ref?.effectName) return `name:${ref.effectName}`;
  return 'unknown';
};

export const getRuntimeTickKey = (combatantId, tickType, ref = {}) => (
  `${combatantId}-${tickType}-${getRuntimeEffectRefKey(ref)}`
);

const CONCENTRATION_QUEUE_TYPES = new Set([
  'concentration_fail',
  'concentration_unconscious',
  'recurring_save',
  'dot_tick',
  'hot_tick',
  'temp_hp_tick',
]);

export const matchesQueuedConcentrationItem = (
  item,
  { casterId = null, spellName = null, effectGroupId = null } = {},
) => {
  if (!item || !CONCENTRATION_QUEUE_TYPES.has(item.type)) return false;

  if (effectGroupId && typeof item.effectGroupId === 'string') {
    return item.effectGroupId === effectGroupId;
  }

  if (item.type === 'concentration_fail' || item.type === 'concentration_unconscious') {
    if (casterId && item.casterId !== casterId) return false;
    if (!spellName) return true;
    return item.spellName === spellName;
  }

  if (item.effectGroupId) return false;
  if (!spellName) return false;
  return item.effectName === spellName;
};

export const removeQueuedConcentrationItems = (
  queueItems = [],
  ref = {},
) => toArray(queueItems).filter((item) => !matchesQueuedConcentrationItem(item, ref));

const matchesRuntimeEffectRef = (entry, ref = {}, index = -1) => {
  if (!entry) return false;
  if (ref?.effectId) {
    return getEntryEffectId(entry) === ref.effectId;
  }
  if (ref?.sourceCollection && Number.isInteger(ref?.effectIndex)) {
    return index === ref.effectIndex;
  }
  if (ref?.effectName) {
    return getCleanupNamesForEntry(entry, ref.effectName).includes(ref.effectName);
  }
  return false;
};

const removeOneRuntimeCondition = (conditions, ref = {}) => {
  let removed = false;
  const nextConditions = toArray(conditions).filter((condition) => {
    if (removed) return true;
    if (typeof condition === 'object' && condition !== null) {
      if (ref.effectId && getEntryEffectId(condition) === ref.effectId) {
        removed = true;
        return false;
      }
      if (ref.effectGroupId && condition.effectGroupId === ref.effectGroupId) {
        removed = true;
        return false;
      }
      const label = typeof condition.name === 'string' ? condition.name : '';
      if (ref.effectName && (label === ref.effectName || label.includes(ref.effectName) || ref.effectName.includes(label))) {
        removed = true;
        return false;
      }
      return true;
    }

    const label = typeof condition === 'string' ? condition : '';
    if (!ref.effectName) return true;
    if (label === ref.effectName || label.includes(ref.effectName) || ref.effectName.includes(label)) {
      removed = true;
      return false;
    }
    return true;
  });

  return { nextConditions, removed };
};

const removeOneRuntimeEntry = (entries, ref = {}) => {
  let removedEntry = null;
  const nextEntries = toArray(entries).filter((entry, index) => {
    if (removedEntry) return true;
    if (matchesRuntimeEffectRef(entry, ref, index)) {
      removedEntry = entry;
      return false;
    }
    return true;
  });
  return {
    nextEntries,
    removedEntry,
  };
};

export const removeRuntimeEffectInstance = ({
  combatants = [],
  chars = {},
  combatantId,
  ref = {},
}) => {
  let nextCombatants = toArray(combatants);
  let nextChars = { ...(chars || {}) };
  const normalizedRef = createRuntimeEffectRef(ref);
  let removed = false;

  const removeFromCombatDebuffs = normalizedRef.sourceCollection === 'combatant.debuffs' || !normalizedRef.sourceCollection;
  if (removeFromCombatDebuffs) {
    const combatantIndex = nextCombatants.findIndex((entry) => entry?.id === combatantId);
    if (combatantIndex >= 0) {
      const combatant = nextCombatants[combatantIndex];
      const { nextEntries, removedEntry } = removeOneRuntimeEntry(combatant?.debuffs, normalizedRef);
      if (removedEntry) {
        const cleanupRef = createRuntimeEffectRef({
          ...normalizedRef,
          effectId: normalizedRef.effectId || getEntryEffectId(removedEntry),
          effectGroupId: normalizedRef.effectGroupId || removedEntry?.effectGroupId || null,
          effectName: normalizedRef.effectName || removedEntry?.name || null,
        });
        const { nextConditions } = removeOneRuntimeCondition(combatant?.conditions, cleanupRef);
        nextCombatants = nextCombatants.map((entry, index) => (
          index === combatantIndex
            ? { ...entry, debuffs: nextEntries, conditions: nextConditions }
            : entry
        ));
        removed = true;
      }
    }
  }

  const removeFromActiveEffects = normalizedRef.sourceCollection === 'char.activeEffects' || (!normalizedRef.sourceCollection && !removed);
  if (removeFromActiveEffects) {
    const targetChar = nextChars?.[combatantId];
    if (targetChar) {
      const { nextEntries, removedEntry } = removeOneRuntimeEntry(targetChar?.activeEffects, normalizedRef);
      if (removedEntry) {
        nextChars[combatantId] = {
          ...targetChar,
          activeEffects: nextEntries,
        };
        removed = true;
      }
    }
  }

  return {
    combatants: nextCombatants,
    chars: nextChars,
    removed,
  };
};

export const removeLinkedConcentrationState = ({
  casterId,
  spellName = null,
  effectGroupId = null,
  combatants = [],
  chars = {},
}) => {
  const removedTargetIds = new Set();
  const charConditionRemovals = new Map();

  const updatedCombatants = toArray(combatants).map((combatant) => {
    const beforeDebuffs = toArray(combatant.debuffs);
    const removedDebuffs = beforeDebuffs.filter((entry) => (
      isLinkedConcentrationEffect(entry, { casterId, spellName, effectGroupId })
    ));
    const afterDebuffs = removedDebuffs.length > 0
      ? beforeDebuffs.filter((entry) => !isLinkedConcentrationEffect(entry, { casterId, spellName, effectGroupId }))
      : beforeDebuffs;

    const blockedNames = new Set();
    addCleanupName(blockedNames, spellName);
    removedDebuffs.forEach((entry) => {
      getCleanupNamesForEntry(entry).forEach((name) => blockedNames.add(name));
    });
    const conditionRemovalCounts = buildLegacyRemovalCounts(removedDebuffs, spellName);

    const beforeConditions = toArray(combatant.conditions);
    const afterConditions = effectGroupId
      ? consumeLegacyLabels(beforeConditions, conditionRemovalCounts)
      : filterConditionLabels(beforeConditions, blockedNames);
    if (removedDebuffs.length > 0 || afterConditions.length !== beforeConditions.length) {
      removedTargetIds.add(combatant.id);
      return {
        ...combatant,
        debuffs: afterDebuffs,
        conditions: afterConditions,
      };
    }
    return combatant;
  });

  const updatedChars = { ...chars };
  Object.keys(updatedChars).forEach((charId) => {
    const currentChar = updatedChars[charId];
    if (!currentChar) return;

    const beforeEffects = toArray(currentChar.activeEffects);
    const removedEffects = beforeEffects.filter((entry) => (
      isLinkedConcentrationEffect(entry, { casterId, spellName, effectGroupId })
    ));
    const afterEffects = removedEffects.length > 0
      ? beforeEffects.filter((entry) => !isLinkedConcentrationEffect(entry, { casterId, spellName, effectGroupId }))
      : beforeEffects;

    const blockedNames = new Set();
    addCleanupName(blockedNames, spellName);
    removedEffects.forEach((entry) => {
      getCleanupNamesForEntry(entry).forEach((name) => blockedNames.add(name));
    });
    const legacyRemovalCounts = buildLegacyRemovalCounts(removedEffects, spellName);
    if (removedEffects.length > 0) {
      charConditionRemovals.set(charId, {
        removalCounts: legacyRemovalCounts,
        blockedNames: [...blockedNames],
      });
    }

    let nextChar = currentChar;
    let changed = false;

    if (afterEffects.length !== beforeEffects.length) {
      nextChar = { ...nextChar, activeEffects: afterEffects };
      changed = true;
      removedTargetIds.add(charId);
    }

    const beforeBuffList = toArray(nextChar.buffList);
    const afterBuffList = effectGroupId
      ? consumeLegacyLabels(beforeBuffList, legacyRemovalCounts)
      : (blockedNames.size > 0
        ? beforeBuffList.filter((buff) => ![...blockedNames].some((name) => buff.includes(name)))
        : beforeBuffList);
    if (afterBuffList.length !== beforeBuffList.length) {
      nextChar = { ...nextChar, buffList: afterBuffList };
      changed = true;
      removedTargetIds.add(charId);
    }

    const sourceMap = nextChar.buffSources || {};
    const nextSourceMap = { ...sourceMap };
    const remainingSourceCounts = new Map(legacyRemovalCounts);
    Object.keys(nextSourceMap).forEach((key) => {
      const exactCount = remainingSourceCounts.get(key) || 0;
      if (exactCount > 0) {
        remainingSourceCounts.set(key, exactCount - 1);
        delete nextSourceMap[key];
        return;
      }
      if (!effectGroupId && [...blockedNames].some((name) => key.includes(name))) {
        delete nextSourceMap[key];
      }
    });
    if (Object.keys(nextSourceMap).length !== Object.keys(sourceMap).length) {
      nextChar = { ...nextChar, buffSources: nextSourceMap };
      changed = true;
      removedTargetIds.add(charId);
    }

    if (charId === casterId) {
      const beforeMarks = toArray(nextChar.markedTargets);
      const removedMarks = beforeMarks.filter((entry) => (
        isLinkedConcentrationMark(entry, { casterId, spellName, effectGroupId })
      ));
      const afterMarks = removedMarks.length > 0
        ? beforeMarks.filter((entry) => !isLinkedConcentrationMark(entry, { casterId, spellName, effectGroupId }))
        : beforeMarks;
      if (afterMarks.length !== beforeMarks.length) {
        nextChar = { ...nextChar, markedTargets: afterMarks };
        changed = true;
        removedMarks.forEach((mark) => {
          if (mark?.targetId) removedTargetIds.add(mark.targetId);
        });
      }

      if (nextChar.concentratingOn || nextChar.concentrationEffectGroupId) {
        nextChar = {
          ...nextChar,
          concentratingOn: null,
          concentrationEffectGroupId: null,
        };
        changed = true;
      }
    }

    if (changed) {
      updatedChars[charId] = nextChar;
    }
  });

  const finalCombatants = updatedCombatants.map((combatant) => {
    const charRemoval = charConditionRemovals.get(combatant.id);
    if (!charRemoval) return combatant;
    const beforeConditions = toArray(combatant.conditions);
    const afterConditions = effectGroupId
      ? consumeLegacyLabels(beforeConditions, charRemoval.removalCounts)
      : filterConditionLabels(beforeConditions, new Set(charRemoval.blockedNames));
    if (afterConditions.length !== beforeConditions.length) {
      removedTargetIds.add(combatant.id);
      return {
        ...combatant,
        conditions: afterConditions,
      };
    }
    return combatant;
  });

  return {
    combatants: finalCombatants,
    chars: updatedChars,
    removedTargetIds: [...removedTargetIds],
  };
};
